import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Returns a callback with a **stable identity** that always invokes the latest
 * closure. Use it for event handlers passed to memoized children so a changing
 * handler identity doesn't defeat their `React.memo` (e.g. the fabric cards).
 *
 * This is the idiomatic userland equivalent of React's `useEffectEvent`: the
 * returned function never changes between renders, but calling it runs the most
 * recent version with up-to-date props/state. It must only be called from event
 * handlers / effects, never during render.
 */
export function useEventCallback<Args extends unknown[], Return>(fn: (...args: Args) => Return) {
  const ref = useRef(fn);

  useLayoutEffect(() => {
    ref.current = fn;
  });

  return useCallback((...args: Args) => ref.current(...args), []);
}
